import React, { useEffect, useRef } from 'react';

interface StarfieldProps {
  density?: number;
  speed?: number;
  className?: string;
  colors?: string[];
}

const Starfield: React.FC<StarfieldProps> = ({
  density = 300,
  speed = 0.8,
  className = '',
  colors = ['#ffffff', '#a855f7', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b']
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create stars with enhanced properties
    const stars: Array<{
      x: number;
      y: number;
      z: number;
      prevX: number;
      prevY: number;
      color: string;
      brightness: number;
      twinkle: number;
      twinkleSpeed: number;
      type: 'normal' | 'bright' | 'giant';
    }> = [];

    for (let i = 0; i < density; i++) {
      const type = Math.random() < 0.1 ? 'giant' : Math.random() < 0.3 ? 'bright' : 'normal';
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        z: Math.random() * 1000,
        prevX: 0,
        prevY: 0,
        color: colors[Math.floor(Math.random() * colors.length)],
        brightness: type === 'giant' ? 1 : type === 'bright' ? 0.8 : Math.random() * 0.6 + 0.2,
        twinkle: Math.random() * Math.PI * 2,
        twinkleSpeed: Math.random() * 0.05 + 0.02,
        type
      });
    }

    const animate = () => {
      // Create trailing effect instead of complete clear
      ctx.fillStyle = 'rgba(10, 14, 26, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      stars.forEach((star) => {
        star.prevX = star.x;
        star.prevY = star.y;

        star.z -= speed;
        if (star.z <= 0) {
          star.x = Math.random() * canvas.width;
          star.y = Math.random() * canvas.height;
          star.z = 1000;
          star.color = colors[Math.floor(Math.random() * colors.length)];
        }

        // Calculate 3D perspective position
        const x = (star.x - centerX) * (1000 / star.z) + centerX;
        const y = (star.y - centerY) * (1000 / star.z) + centerY;

        // Update twinkle animation
        star.twinkle += star.twinkleSpeed;
        const twinkleIntensity = (Math.sin(star.twinkle) + 1) / 2;

        // Calculate star size and opacity based on distance and type
        const distance = (1000 - star.z) / 1000;
        let baseSize = distance * (star.type === 'giant' ? 6 : star.type === 'bright' ? 3 : 2);
        const opacity = Math.min(distance * star.brightness * twinkleIntensity, 1);

        if (x >= -50 && x <= canvas.width + 50 && y >= -50 && y <= canvas.height + 50) {
          // Draw hyperspace trail for fast-moving stars
          if (distance > 0.5) {
            ctx.strokeStyle = star.color;
            ctx.globalAlpha = opacity * 0.4;
            ctx.lineWidth = Math.max(baseSize * 0.5, 1);
            ctx.beginPath();
            ctx.moveTo(star.prevX, star.prevY);
            ctx.lineTo(x, y);
            ctx.stroke();
          }

          // Enhanced glow effect for special stars
          if (star.type === 'giant' || (star.type === 'bright' && distance > 0.6)) {
            const glowSize = baseSize * (star.type === 'giant' ? 4 : 2.5);
            const gradient = ctx.createRadialGradient(x, y, 0, x, y, glowSize);
            gradient.addColorStop(0, star.color);
            gradient.addColorStop(0.4, star.color + '40');
            gradient.addColorStop(1, 'transparent');
            ctx.fillStyle = gradient;
            ctx.globalAlpha = opacity * 0.6;
            ctx.beginPath();
            ctx.arc(x, y, glowSize, 0, Math.PI * 2);
            ctx.fill();
          }

          // Draw the main star
          ctx.fillStyle = star.color;
          ctx.globalAlpha = opacity;
          ctx.beginPath();
          ctx.arc(x, y, Math.max(baseSize, 0.5), 0, Math.PI * 2);
          ctx.fill();

          // Add cross sparkle for giant and bright stars
          if ((star.type === 'giant' || star.type === 'bright') && twinkleIntensity > 0.6) {
            ctx.strokeStyle = star.color;
            ctx.globalAlpha = opacity * 0.8;
            ctx.lineWidth = star.type === 'giant' ? 2 : 1;
            const sparkleSize = baseSize * (star.type === 'giant' ? 3 : 2);
            ctx.beginPath();
            ctx.moveTo(x - sparkleSize, y);
            ctx.lineTo(x + sparkleSize, y);
            ctx.moveTo(x, y - sparkleSize);
            ctx.lineTo(x, y + sparkleSize);
            ctx.stroke();
          }
        }
      });

      ctx.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [density, speed, colors])

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 pointer-events-none z-0 ${className}`}
      style={{ background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #0a0e1a 100%)' }}
    />
  );
};

export default Starfield;